/**
 * Approximate geographic centroids for countries, used to place map markers.
 * Row format: [ISO 3166-1 alpha-2 code, canonical name, latitude, longitude, ...aliases]
 */
const COUNTRY_ROWS = [
    ['AF', 'Afghanistan', 33.9391, 67.7100],
    ['AE', 'United Arab Emirates', 23.4241, 53.8478, 'uae', 'emirates'],
    ['AR', 'Argentina', -38.4161, -63.6167],
    ['AM', 'Armenia', 40.0691, 45.0382],
    ['AT', 'Austria', 47.5162, 14.5501],
    ['AU', 'Australia', -25.2744, 133.7751],
    ['AZ', 'Azerbaijan', 40.1431, 47.5769],
    ['BD', 'Bangladesh', 23.6850, 90.3563],
    ['BE', 'Belgium', 50.5039, 4.4699],
    ['BG', 'Bulgaria', 42.7339, 25.4858],
    ['BR', 'Brazil', -14.2350, -51.9253],
    ['BY', 'Belarus', 53.7098, 27.9534],
    ['CA', 'Canada', 56.1304, -106.3468],
    ['CH', 'Switzerland', 46.8182, 8.2275],
    ['CL', 'Chile', -35.6751, -71.5430],
    ['CN', 'China', 35.8617, 104.1954, 'prc', "people's republic of china", 'peoples republic of china'],
    ['CO', 'Colombia', 4.5709, -74.2973],
    ['CU', 'Cuba', 21.5218, -77.7812],
    ['CZ', 'Czech Republic', 49.8175, 15.4730, 'czechia'],
    ['DE', 'Germany', 51.1657, 10.4515],
    ['DK', 'Denmark', 56.2639, 9.5018],
    ['DZ', 'Algeria', 28.0339, 1.6596],
    ['EE', 'Estonia', 58.5953, 25.0136],
    ['EG', 'Egypt', 26.8206, 30.8025],
    ['ES', 'Spain', 40.4637, -3.7492],
    ['ET', 'Ethiopia', 9.1450, 40.4897],
    ['FI', 'Finland', 61.9241, 25.7482],
    ['FR', 'France', 46.6034, 1.8883],
    ['GB', 'United Kingdom', 55.3781, -3.4360, 'uk', 'great britain', 'britain', 'england', 'scotland', 'wales'],
    ['GE', 'Georgia', 42.3154, 43.3569],
    ['GR', 'Greece', 39.0742, 21.8243],
    ['HK', 'Hong Kong', 22.3193, 114.1694],
    ['HU', 'Hungary', 47.1625, 19.5033],
    ['ID', 'Indonesia', -0.7893, 113.9213],
    ['IE', 'Ireland', 53.4129, -8.2439],
    ['IL', 'Israel', 31.0461, 34.8516],
    ['IN', 'India', 20.5937, 78.9629],
    ['IQ', 'Iraq', 33.2232, 43.6793],
    ['IR', 'Iran', 32.4279, 53.6880, 'islamic republic of iran', 'persia'],
    ['IT', 'Italy', 41.8719, 12.5674],
    ['JO', 'Jordan', 30.5852, 36.2384],
    ['JP', 'Japan', 36.2048, 138.2529],
    ['KE', 'Kenya', -0.0236, 37.9062],
    ['KP', 'North Korea', 40.3399, 127.5101, 'dprk', "democratic people's republic of korea"],
    ['KR', 'South Korea', 35.9078, 127.7669, 'rok', 'republic of korea', 'korea'],
    ['KZ', 'Kazakhstan', 48.0196, 66.9237],
    ['LB', 'Lebanon', 33.8547, 35.8623],
    ['LT', 'Lithuania', 55.1694, 23.8813],
    ['LV', 'Latvia', 56.8796, 24.6032],
    ['LY', 'Libya', 26.3351, 17.2283],
    ['MA', 'Morocco', 31.7917, -7.0926],
    ['MD', 'Moldova', 47.4116, 28.3699],
    ['MX', 'Mexico', 23.6345, -102.5528],
    ['MY', 'Malaysia', 4.2105, 101.9758],
    ['NG', 'Nigeria', 9.0820, 8.6753],
    ['NL', 'Netherlands', 52.1326, 5.2913, 'holland'],
    ['NO', 'Norway', 60.4720, 8.4689],
    ['NZ', 'New Zealand', -40.9006, 174.8860],
    ['PH', 'Philippines', 12.8797, 121.7740],
    ['PK', 'Pakistan', 30.3753, 69.3451],
    ['PL', 'Poland', 51.9194, 19.1451],
    ['PT', 'Portugal', 39.3999, -8.2245],
    ['QA', 'Qatar', 25.3548, 51.1839],
    ['RO', 'Romania', 45.9432, 24.9668],
    ['RS', 'Serbia', 44.0165, 21.0059],
    ['RU', 'Russia', 61.5240, 105.3188, 'russian federation'],
    ['SA', 'Saudi Arabia', 23.8859, 45.0792],
    ['SE', 'Sweden', 60.1282, 18.6435],
    ['SG', 'Singapore', 1.3521, 103.8198],
    ['SY', 'Syria', 34.8021, 38.9968],
    ['TH', 'Thailand', 15.8700, 100.9925],
    ['TR', 'Turkey', 38.9637, 35.2433, 'turkiye'],
    ['TW', 'Taiwan', 23.6978, 120.9605],
    ['UA', 'Ukraine', 48.3794, 31.1656],
    ['US', 'United States', 39.8283, -98.5795, 'usa', 'us', 'america', 'united states of america'],
    ['VE', 'Venezuela', 6.4238, -66.5897],
    ['VN', 'Vietnam', 14.0583, 108.2772, 'viet nam'],
    ['YE', 'Yemen', 15.5527, 48.5164],
    ['ZA', 'South Africa', -30.5595, 22.9375],
];

/**
 * Normalize a place name for lookups: lowercase, drop periods ("U.S." -> "us"),
 * strip a leading "the", and collapse whitespace.
 * @param {string} name
 * @returns {string}
 */
export const normalizePlaceName = (name) =>
    String(name || '')
        .toLowerCase()
        .replace(/\./g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^the /, '');

/** Build name/alias and ISO-code indexes once at module load. */
const buildIndexes = (rows) => {
    const byName = new Map();
    const byCode = new Map();
    rows.forEach(([code, name, lat, lng, ...aliases]) => {
        const country = { code, name, coordinates: [lat, lng] };
        byCode.set(code, country);
        [name, ...aliases].forEach((alias) => byName.set(normalizePlaceName(alias), country));
    });
    return { byName, byCode };
};

const { byName, byCode } = buildIndexes(COUNTRY_ROWS);

/**
 * Find a country by name/alias, falling back to an ISO alpha-2 code.
 * @param {string} name - country or place name as extracted from a document
 * @param {string} [isoCode] - optional ISO 3166-1 alpha-2 code
 * @returns {{code: string, name: string, coordinates: number[]} | null}
 */
export const lookupCountry = (name, isoCode) =>
    byName.get(normalizePlaceName(name)) ||
    (isoCode ? byCode.get(String(isoCode).trim().toUpperCase()) : undefined) ||
    null;

export const COUNTRY_COUNT = COUNTRY_ROWS.length;
